import { useApi } from './api';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  audio_url?: string;
  attachments_url?: string;
  attachments_type?: string;
  timestamp: string;
  created_at?: string;
}

export interface Conversation {
  id: string;
  title: string;
  updated_at: string;
}

export const useConversationApi = () => {
  const { fetchWithAuth } = useApi();
  
  /**
   * Load a conversation by ID
   * @param conversationId The ID of the conversation to load
   * @returns The conversation messages
   */
  const loadConversation = async (conversationId: string): Promise<ChatMessage[]> => {
    try {
      const data = await fetchWithAuth(`/chat/history?conversation_id=${conversationId}`);
      
      // Transform data to chat message format
      return data.map((msg: any) => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        attachments_url: msg.attachments_url,
        attachments_type: msg.attachments_type,
        timestamp: msg.created_at.includes('T') && !msg.created_at.endsWith('Z') 
          ? msg.created_at + 'Z' 
          : msg.created_at
      }));
    } catch (error) {
      console.error('Error loading conversation:', error);
      throw new Error('Failed to load conversation history');
    }
  };

  /**
   * Rename a conversation
   * @param conversationId The ID of the conversation to rename
   * @param newTitle The new title for the conversation
   * @returns True if successful, false otherwise
   */
  const renameConversation = async (conversationId: string, newTitle: string): Promise<boolean> => {
    try {
      await fetchWithAuth(`/chat/conversations/${conversationId}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title: newTitle })
      });
      return true;
    } catch (error) {
      console.error('Error renaming conversation:', error);
      return false;
    }
  };

  /**
   * Delete a conversation
   * @param conversationId The ID of the conversation to delete
   * @returns True if successful, false otherwise
   */
  const deleteConversation = async (conversationId: string): Promise<boolean> => {
    try {
      await fetchWithAuth(`/chat/conversations/${conversationId}`, {
        method: 'DELETE'
      });
      return true;
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return false;
    }
  };

  /**
   * Fetch conversations list with pagination
   * @param page The page number to fetch
   * @returns List of conversations
   */
  const fetchConversations = async (page: number = 0): Promise<{ conversations: Conversation[], has_more: boolean }> => {
    try {
      return await fetchWithAuth(`/chat/conversations?page=${page}`);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return { conversations: [], has_more: false };
    }
  };

  return {
    loadConversation,
    renameConversation,
    deleteConversation,
    fetchConversations
  };
}; 